/**
 * Vignette uniforme d'un widget (catalogue d'ajout) : le widget est rendu à sa
 * taille réelle puis mis à l'échelle pour TENIR (contain) dans la boîte parente,
 * centré. Toutes les vignettes occupent ainsi la même boîte, quelle que soit la
 * forme du widget.
 */
import {
  ComponentType,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { WidgetTypeId } from "./widgetCatalog";
import { getWidgetPreviewDimensions, WIDGET_COMPONENTS } from "./WidgetPreview";
import { WidgetPreviewContext } from "./widgets/WidgetShell";

type WidgetThumbnailProps = {
  widgetType: WidgetTypeId;
  props: Record<string, unknown>;
  w: number;
  h: number;
};

export function WidgetThumbnail({
  widgetType,
  props,
  w,
  h,
}: WidgetThumbnailProps) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const Component = WIDGET_COMPONENTS[widgetType] as ComponentType<any>;

  const dims = getWidgetPreviewDimensions(w, h);
  const containerRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(0);

  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const update = () => {
      const bw = el.clientWidth;
      const bh = el.clientHeight;
      if (bw > 0 && bh > 0) {
        setScale(Math.min(1, bw / dims.width, bh / dims.height));
      }
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, [dims.width, dims.height]);

  if (!Component) return null;

  return (
    <div
      ref={containerRef}
      className="w-full h-full flex items-center justify-center overflow-hidden"
    >
      {/* Empreinte mise à l'échelle (occupe la place réelle du widget réduit). */}
      <div
        className="shrink-0"
        style={{ width: dims.width * scale, height: dims.height * scale }}
      >
        <div
          className="origin-top-left pointer-events-none"
          style={{
            width: dims.width,
            height: dims.height,
            transform: `scale(${scale})`,
          }}
        >
          <WidgetPreviewContext.Provider value={true}>
            <Component {...props} />
          </WidgetPreviewContext.Provider>
        </div>
      </div>
    </div>
  );
}
